import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart, Cell } from 'recharts';
import { TrendingUp, TrendingDown } from 'lucide-react';

const TradingChart = ({ trades, ticker }) => {
  // Prepare chart data from trades
  const prepareChartData = () => {
    if (!trades || trades.length === 0) {
      // Mock data for demonstration
      return Array.from({ length: 20 }, (_, i) => ({
        time: `${i + 1}`,
        price: 2450 + Math.random() * 50 - 25,
        buy: i % 5 === 0 ? 2450 + Math.random() * 20 : null,
        sell: i % 7 === 0 ? 2450 + Math.random() * 20 : null,
      }));
    }

    return trades.slice(0, 20).reverse().map((trade, idx) => ({
      time: new Date(trade.entry_time).toLocaleTimeString(),
      price: trade.entry_price,
      buy: trade.side === 'buy' ? trade.entry_price : null,
      sell: trade.side === 'sell' ? trade.entry_price : null,
      profit: trade.profit_loss || 0,
    }));
  };

  // Prepare P/L chart data
  const prepareProfitData = () => {
    if (!trades || trades.length === 0) {
      return Array.from({ length: 10 }, (_, i) => ({
        trade: `T${i + 1}`,
        profit: Math.random() * 200 - 100,
      }));
    }

    return trades.filter(t => t.profit_loss !== null).slice(0, 10).map((trade, idx) => ({
      trade: `T${idx + 1}`,
      profit: trade.profit_loss,
    }));
  };

  // Prepare trade distribution
  const prepareDistribution = () => {
    const buyCount = trades.filter(t => t.side === 'buy').length;
    const sellCount = trades.filter(t => t.side === 'sell').length;

    if (buyCount === 0 && sellCount === 0) {
      return [
        { name: 'BUY', value: 5, color: '#10b981' },
        { name: 'SELL', value: 3, color: '#ef4444' },
      ];
    }

    return [
      { name: 'BUY', value: buyCount, color: '#10b981' },
      { name: 'SELL', value: sellCount, color: '#ef4444' },
    ];
  };

  const chartData = prepareChartData();
  const profitData = prepareProfitData();
  const distributionData = prepareDistribution();

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-800 border border-gray-600 rounded-lg p-3 shadow-lg">
          <p className="text-white font-semibold">₹{payload[0].value?.toFixed(2)}</p>
          {payload[0].payload.buy && (
            <p className="text-green-400 text-sm flex items-center gap-1">
              <TrendingUp size={14} /> BUY Signal
            </p>
          )}
          {payload[0].payload.sell && (
            <p className="text-red-400 text-sm flex items-center gap-1">
              <TrendingDown size={14} /> SELL Signal
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Price Chart with Buy/Sell Signals */}
      <div className="bg-gray-800/50 backdrop-blur rounded-lg p-6 border border-gray-700">
        <h2 className="text-xl font-bold mb-4 text-blue-400">📈 Price Chart with Trading Signals</h2>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="time" stroke="#9ca3af" />
            <YAxis stroke="#9ca3af" />
            <Tooltip content={<CustomTooltip />} />
            <Area 
              type="monotone" 
              dataKey="price" 
              stroke="#3b82f6" 
              fillOpacity={1} 
              fill="url(#colorPrice)" 
            />
            <Line 
              type="monotone" 
              dataKey="buy" 
              stroke="#10b981" 
              strokeWidth={0}
              dot={{ fill: '#10b981', r: 6 }} 
            />
            <Line 
              type="monotone" 
              dataKey="sell" 
              stroke="#ef4444" 
              strokeWidth={0}
              dot={{ fill: '#ef4444', r: 6 }} 
            />
          </AreaChart>
        </ResponsiveContainer>
        <div className="flex gap-4 mt-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-gray-400">BUY Signal</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span className="text-gray-400">SELL Signal</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profit/Loss Chart */}
        <div className="bg-gray-800/50 backdrop-blur rounded-lg p-6 border border-gray-700">
          <h2 className="text-xl font-bold mb-4 text-green-400">💰 Profit/Loss per Trade</h2>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={profitData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="trade" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                labelStyle={{ color: '#fff' }}
                formatter={(value) => [`₹${value.toFixed(2)}`, 'P/L']}
              />
              <Bar dataKey="profit">
                {profitData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.profit >= 0 ? '#10b981' : '#ef4444'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Trade Distribution */}
        <div className="bg-gray-800/50 backdrop-blur rounded-lg p-6 border border-gray-700">
          <h2 className="text-xl font-bold mb-4 text-purple-400">📊 Trade Distribution</h2>
          <div className="flex items-center justify-center h-[250px]">
            <div className="space-y-4 w-full">
              {distributionData.map((item, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-300 font-semibold">{item.name}</span>
                    <span className="text-gray-400">{item.value} trades</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-8">
                    <div
                      className="h-8 rounded-full flex items-center justify-end pr-3 font-semibold text-white text-sm"
                      style={{
                        width: `${(item.value / distributionData.reduce((sum, d) => sum + d.value, 0)) * 100}%`,
                        backgroundColor: item.color,
                      }}
                    >
                      {((item.value / distributionData.reduce((sum, d) => sum + d.value, 0)) * 100).toFixed(0)}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingChart;
